/**
 * 後端回的「訂單 + 明細」轉成 antd Table 的樹狀列。
 *
 * 主列是一張訂單，子列是它的住宿明細。多數訂單只有一筆明細 (實測 402 張裡 348 張)，
 * 那種就直接攤平成一般列 —— 掛一個永遠只有一個子項的展開鈕只是噪音。
 *
 * 主列上「各明細不一致」的欄位不能亂填一個值。實測多明細訂單裡房型有三分之二不一致、
 * 單價超過一半、動作日也有兩成 —— 隨便取第一筆會讓主列看起來像一筆確定的事實，
 * 實際上是取樣。所以統一時才帶原值 (欄位照常渲染)，不統一時帶 null 並另外給範圍字串或種類數。
 */

const shortDate = (s) => (typeof s === 'string' ? s.slice(5) : s); // YYYY/MM/DD → MM/DD

/** 該欄在這張訂單的明細裡是否只有一個值。是就回那個值，否則回 null。 */
const uniform = (details, key) => {
  const first = details[0][key];
  return details.every((d) => d[key] === first) ? first : null;
};

const distinctCount = (details, key) => new Set(details.map((d) => d[key])).size;

const rangeOf = (details, key, fmt = (v) => v) => {
  const vals = details.map((d) => d[key]).filter((v) => v != null);
  if (!vals.length) return '—';
  const lo = vals.reduce((a, b) => (a < b ? a : b));
  const hi = vals.reduce((a, b) => (a > b ? a : b));
  return `${fmt(lo)}–${fmt(hi)}`;
};

const num = (v) => Math.round(v).toLocaleString();

/**
 * @param {Array} orders 後端 items：每張訂單含 details 陣列與 qnt/nights 合計
 * @returns {Array} antd dataSource；多明細訂單帶 children，單明細訂單直接是明細列
 */
export function toTableRows(orders) {
  return (orders || []).map((o) => {
    const details = o.details || [];
    const rowKey = `${o.ikey}-${o.kind}`;
    if (details.length === 1) {
      // 攤平：訂單層的欄位 (來源/團體/通路編號) 覆蓋不到明細，明細也沒有這些欄位，兩邊合併
      return { ...details[0], ...orderFields(o), _key: rowKey, _parent: false };
    }
    return {
      ...orderFields(o),
      _key: rowKey,
      _parent: true,
      _count: details.length,
      qnt: o.qnt,
      nights: o.nights,
      // 統一才帶值；null 代表「這張訂單各明細不同」，由 _range / _kinds 補上怎麼顯示
      act_dat: uniform(details, 'act_dat'),
      ci: uniform(details, 'ci'),
      co: uniform(details, 'co'),
      stay_nights: uniform(details, 'stay_nights'),
      ovlp: uniform(details, 'ovlp'),
      rent: uniform(details, 'rent'),
      room_nam: uniform(details, 'room_nam'),
      rate_nam: uniform(details, 'rate_nam'),
      _range: {
        act_dat: rangeOf(details, 'act_dat', shortDate),
        ci: rangeOf(details, 'ci', shortDate),
        co: rangeOf(details, 'co', shortDate),
        stay_nights: rangeOf(details, 'stay_nights'),
        ovlp: rangeOf(details, 'ovlp'),
        rent: rangeOf(details, 'rent', num),
      },
      _kinds: {
        room_nam: distinctCount(details, 'room_nam'),
        rate_nam: distinctCount(details, 'rate_nam'),
      },
      children: details.map((d, i) => ({ ...d, ...orderFields(o), _key: `${rowKey}-${d.seq}-${i}`, _parent: false, _child: true })),
    };
  });
}

/** 訂單層欄位：每筆明細都一樣，主列與子列共用。 */
function orderFields(o) {
  return { ikey: o.ikey, kind: o.kind, rsv_nos: o.rsv_nos, party: o.party, party_alt: o.party_alt,
    source: o.source, is_group: o.is_group };
}
